import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export interface PopoverProps {
  /** Controls whether the popover is visible */
  open: boolean;
  /** Callback fired when the popover requests to close */
  onClose: () => void;
  /** Ref to the element that triggered the popover */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Positioning classes for the wrapper (defaults to absolute positioning) */
  className?: string;
  /** Classes applied to the content container inside the popover */
  contentClassName?: string;
  children: React.ReactNode;
}

export const Popover: React.FC<PopoverProps> = ({
  open,
  onClose,
  anchorRef,
  className = '',
  contentClassName = 'bg-white border border-gray-200 rounded-lg',
  children,
}) => {
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  // Calculate position based on anchorRef
  useEffect(() => {
    if (!open || !anchorRef?.current) {
      setPosition(null);
      return;
    }

    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;

      const anchorRect = anchor.getBoundingClientRect();
      const popover = popoverRef.current;
      const content = contentRef.current;
      const prefersAbove = className.includes('bottom-full');
      const prefersRightAlign = className.includes('right-0');
      const popoverWidth = popover?.offsetWidth || content?.offsetWidth || 384;
      const popoverHeight = popover?.offsetHeight || content?.offsetHeight || 200;
      const margin = 8;
      const viewportW = window.innerWidth;
      const viewportH = window.innerHeight;

      const above = anchorRect.top - popoverHeight - margin;
      const below = anchorRect.bottom + margin;
      const spaceAbove = anchorRect.top - margin;
      const spaceBelow = viewportH - anchorRect.bottom - margin;

      let top: number;
      if (prefersAbove) {
        top = spaceAbove >= popoverHeight || spaceAbove >= spaceBelow ? above : below;
      } else {
        top = spaceBelow >= popoverHeight || spaceBelow >= spaceAbove ? below : above;
      }
      top = Math.max(margin, Math.min(top, viewportH - popoverHeight - margin));

      let left = prefersRightAlign ? anchorRect.right - popoverWidth : anchorRect.left;
      left = Math.max(margin, Math.min(left, viewportW - popoverWidth - margin));

      setPosition({ top, left });
    };

    // Initial position calculation
    updatePosition();

    let rafId1: number;
    let rafId2: number;
    let resizeObserver: ResizeObserver | null = null;
    
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(() => {
        updatePosition();
        // Set up ResizeObserver after content is rendered
        if (contentRef.current && !resizeObserver) {
          resizeObserver = new ResizeObserver(() => {
            requestAnimationFrame(() => {
              updatePosition();
            });
          });
          resizeObserver.observe(contentRef.current);
        }
      });
    });

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, anchorRef, className, children]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) {
        return;
      }
      if (anchorRef?.current?.contains(target)) {
        return;
      }
      onClose();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose, anchorRef]);

  if (!open || !position) {
    return null;
  }

  const popoverContent = (
    <div
      ref={popoverRef}
      className="fixed z-[130]"
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
    >
      <div
        ref={contentRef}
        className={contentClassName}
        style={{
          boxShadow: '0 2px 2px rgba(0,0,0,0.05), 0 0 15px rgba(0,0,0,0.09)',
          transition: 'height 0.3s ease-in-out, min-height 0.3s ease-in-out',
          maxHeight: 'calc(100vh - 16px)',
          overflowY: 'auto',
        }}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(popoverContent, document.body);
};
